// FooterV4 · SPA public footer · 1:1 parity with SSR SiteFooter.
//
// Hotfix 2026-10 · aligned to /app/web-ssr/components/SiteFooter.tsx so
// that an anonymous visitor sees the same footer on /marketplace/v10 as
// on /, /pricing, /features and the rest of the SSR marketing surface.
//
// Structure:
//   · Brand row     (GlobalLeadSuite + one-line description)
//   · 4-column grid (PRODUCT · RESOURCES · LEGAL · ACCOUNT)
//   · Divider
//   · Bottom bar    (Nocturna copyright · Sitemap · Feed · Language)
//
// Palette: V12 Achromatic Strict · pure black #000 + border #1A1A1A.

import React from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const CLUSTERS = [
    {
        title: "PRODUCT",
        testid: "footer-cluster-product",
        items: [
            { label: "Features",         href: "/features" },
            { label: "Modules",          href: "/modules" },
            { label: "Marketplace",      href: "/marketplace/v10", testid: "footer-link-marketplace" },
            { label: "Ecosystems",       href: "/ecosystems" },
            { label: "AI Universe",      href: "/ai-universe" },
            { label: "AI Civilization",  href: "/ai-civilization" },
            { label: "Badge System",     href: "/badge-system" },
            { label: "Pricing",          href: "/pricing" },
        ],
    },
    {
        title: "RESOURCES",
        testid: "footer-cluster-resources",
        items: [
            { label: "Docs",          href: "/docs" },
            { label: "API",           href: "/docs/api" },
            { label: "Webhooks",      href: "/docs/webhooks" },
            { label: "Credit Engine", href: "/docs/credits" },
            { label: "Limits",        href: "/docs/limits" },
            { label: "SEO Hub",       href: "/seo" },
            { label: "Community",     href: "/community" },
            { label: "Academy",       href: "/academy" },
        ],
    },
    {
        title: "LEGAL",
        testid: "footer-cluster-legal",
        items: [
            { label: "Privacy",        href: "/privacy" },
            { label: "Terms",          href: "/terms" },
            { label: "Cookie Policy",  href: "/cookies" },
            { label: "Compliance",     href: "/compliance" },
            { label: "SLA",            href: "/sla" },
            { label: "Onboarding",     href: "/onboarding" },
            { label: "Contact",        href: "/contact" },
        ],
    },
    {
        title: "ACCOUNT",
        testid: "footer-cluster-account",
        items: [
            { label: "Login",      href: "/login" },
            { label: "Register",   href: "/register" },
            { label: "Dashboard",  href: "/dashboard" },
            { label: "Billing",    href: "/dashboard/billing" },
            { label: "Credits",    href: "/dashboard/credits" },
            { label: "Campaigns",  href: "/dashboard/campaigns" },
        ],
    },
];

export default function FooterV4() {
    // Phase VI.5 Wave A · hide public footer on V12 Workspace aliases
    // for authenticated users (V12UserLayout owns its own chrome).
    const { user } = useAuth();
    const { pathname } = useLocation();
    if (user && pathname && (pathname.startsWith("/app/") || pathname.startsWith("/v2/"))) {
        return null;
    }

    const year = new Date().getFullYear();

    return (
        <footer
            data-testid="footer-v4"
            className="mt-24 border-t"
            style={{
                backgroundColor: "#000000",
                borderColor: "#1A1A1A",
            }}
        >
            <div className="mx-auto max-w-7xl px-6 py-16">
                {/* Brand row · GlobalLeadSuite + tagline */}
                <div className="mb-12 flex flex-col gap-3">
                    <a
                        href="/"
                        data-testid="footer-logo"
                        className="font-semibold tracking-tight text-[#FFFFFF] no-underline"
                    >
                        GlobalLeadSuite
                    </a>
                    <p
                        data-testid="footer-tagline"
                        className="max-w-md text-xs leading-relaxed text-[#A1A1AA]"
                    >
                        AI-first B2B lead generation. Autonomous AI SaaS platform for lead
                        discovery, outreach, and sales — in USD, globally, 24/7.
                    </p>
                </div>

                {/* 4-column grid */}
                <div
                    className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4"
                    data-testid="footer-grid"
                >
                    {CLUSTERS.map((c) => (
                        <div
                            key={c.title}
                            className="flex flex-col gap-3"
                            data-testid={c.testid}
                        >
                            <p className="text-xs font-semibold tracking-[0.16em] text-[#FFFFFF]">
                                {c.title}
                            </p>
                            <ul className="flex flex-col gap-2.5 text-xs text-[#A1A1AA]">
                                {c.items.map((l) => (
                                    <li key={l.href}>
                                        {/* Hotfix 2026-10 · plain <a href> instead of
                                            react-router <Link> so the href is NEVER
                                            prefixed by the SPA basename (/it, /de, ...).
                                            Pixel-identical behavior to the SSR SiteFooter. */}
                                        <a
                                            href={l.href}
                                            data-testid={
                                                l.testid
                                                || `footer-link-${l.href.replace(/\//g, "-").replace(/^-/, "")}`
                                            }
                                            className="transition-colors hover:text-[#FFFFFF] no-underline"
                                        >
                                            {l.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                {/* Divider */}
                <div
                    className="my-10 h-px"
                    style={{ background: "#1A1A1A" }}
                />

                {/* Bottom bar · copyright · sitemap · feed · language */}
                <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                    <p
                        className="text-xs text-[#A1A1AA]"
                        data-testid="footer-copyright"
                    >
                        © {year} Nocturna Network LTD · Company 16846806 · VAT 506869363
                    </p>
                    <div className="flex items-center gap-6 text-xs text-[#A1A1AA]">
                        <a
                            href="/sitemap.xml"
                            className="transition-colors hover:text-[#FFFFFF] no-underline"
                            data-testid="footer-sitemap"
                        >
                            Sitemap
                        </a>
                        <a
                            href="/feed.xml"
                            className="transition-colors hover:text-[#FFFFFF] no-underline"
                            data-testid="footer-feed"
                        >
                            Feed
                        </a>
                        <LanguageSwitcher />
                    </div>
                </div>
            </div>
        </footer>
    );
}
